<template>
  <div class="tinymce-editor">
    <editor :id="id" v-model="editorContent" :init="init" :disabled="disabled"></editor>
  </div>
</template>

<script>
import { tinyImagesUpload } from "./fun"
import { uploadToOss } from "@/utils/uploadFile"
import tinymce from "tinymce/tinymce"
import Editor from "@tinymce/tinymce-vue"
import "tinymce/themes/silver"
// 编辑器插件plugins
// 更多插件参考：https://www.tiny.cloud/docs/plugins/
import "tinymce/plugins/image" // 插入上传图片插件
import "tinymce/plugins/paste" // 插入上传图片插件
// import "tinymce/plugins/imagetools" // 图片编辑工具
import "tinymce/plugins/table" // 插入表格插件
import "tinymce/plugins/lists" // 列表插件
import "tinymce/plugins/wordcount" // 字数统计插件
import "tinymce/plugins/print" // 打印插件
import "tinymce/plugins/fullscreen" // 打印插件
import "tinymce/plugins/insertdatetime" // 打印插件
import "tinymce/plugins/code" // 查看源码
import "tinymce/plugins/advlist" // 有序列表、无序列表的样式
import "tinymce/plugins/nonbreaking" // 改变tab键的默认行为(默认是切换到下一个元素的focus)，在光标之后添加三个空格
import "tinymce/plugins/hr" // 分割线
// import "tinymce/plugins/indent2em"
// import "tinymce/plugins/lineheight"
import "./plugin/dynamicTable" // 动态表格
import "./plugin/watermark" // 水印
import "./plugin/preview" // 修改部分内容后的预览
import { randomString } from "@/utils/common"
import { verifyFields } from "@/views/Backend/AdminEdit/PrintTemplate/static"

export default {
  name: "Tinymce",
  components: {
    Editor
  },
  props: {
    // 用于避免发送oss请求，打印时不需要
    ignoreOSS: {
      type: Boolean,
      default: false
    },
    refSheets: {
      type: Array,
      default: () => []
    },
    value: {
      type: String,
      default: ""
    },
    // 基本路径，默认为空根目录，如果你的项目发布后的地址为目录形式，
    // 即abc.com/tinymce，baseUrl需要配置成tinymce，不然发布后资源会找不到
    baseUrl: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    plugins: {
      type: [String, Array],
      default:
        "lists image table wordcount preview print fullscreen insertdatetime code advlist nonbreaking dynamicTable watermark hr paste"
    },
    toolbar: {
      type: [String, Array],
      default:
        "undo redo | formatselect | fontsizeselect bold italic underline forecolor backcolor removeformat | hr alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | lists image table dynamicTable watermark verifyButton| fullscreen"
    }
  },
  data() {
    return {
      verifyFields,
      id: randomString(6),
      editorContent: this.value,
      options: [
        { value: "one", text: "1" },
        { value: "two", text: "2" }
      ],
      editor: null,
      init: {
        language_url: `${this.baseUrl}/tinymce/langs/zh_CN.js`,
        language: "zh_CN",
        skin_url: `${this.baseUrl}/tinymce/skins/ui/oxide`,
        content_css: `${this.baseUrl}/tinymce/skins/content/default/content.css, ${this.baseUrl}/tinymce/skins/content/default/custom.css`,
        height: "calc(100vh - 60px)",
        plugins: this.plugins,
        toolbar: this.toolbar,
        branding: false,
        resize: false,
        paste_data_images: true,
        menu: {
          file: { title: "File", items: "newdocument restoredraft | print" },
          edit: {
            title: "Edit",
            items: "undo redo | cut copy paste | selectall | searchreplace"
          },
          view: {
            title: "View",
            items: "code | visualaid visualchars visualblocks | spellchecker | fullscreen"
          },
          insert: {
            title: "Insert",
            items:
              "image link media template codesample inserttable dynamicTable | charmap emoticons hr | pagebreak nonbreaking anchor toc | insertdatetime | watermark"
          },
          format: {
            title: "Format",
            items:
              "bold italic underline strikethrough superscript subscript codeformat | formats blockformats fontformats fontsizes align | forecolor backcolor | removeformat"
          },
          tools: {
            title: "Tools",
            items: "spellchecker spellcheckerlanguage | code wordcount"
          },
          table: {
            title: "Table",
            items: "inserttable tableprops deletetable row column cell"
          },
          help: { title: "Help", items: "help" }
        },
        menubar: "file edit format insert tools",
        // automatic_uploads: false, // 关闭自动上传
        images_reuse_filename: true, // 允许复用文件名称
        images_upload_handler: tinyImagesUpload,
        image_advtab: true, // 图片高级设置
        // 插入日期/日期时间的格式
        insertdatetime_formats: [
          "%Y-%m-%d %H:%M:%S",
          "%Y-%m-%d %H:%M:%S %a",
          "%Y-%m-%d",
          "%H:%M:%S"
        ],
        nonbreaking_force_tab: true, // 改变tab的默认行为(默认是切到下一个html元素)，按tab键时插入三个空格
        toolbar_drawer: false, // toolbar列表过长时，收缩成 "..."，点击可展开收缩的toolbar
        fontsize_formats: "11px 12px 14px 16px 18px 20px 24px 36px 48px",
        // Additional options here

        // 变量标签可设置基本样式
        // 参考 https://github.com/tinymce/tinymce/issues/3355
        setup: editor => {
          let _this = this
          var toDateHtml = function() {
            return _this.insertVerifyTable()
          }
          const $ = tinymce.dom.DomQuery
          editor.on("BeforeExecCommand", function(e) {
            $(editor.getBody())
              .find(".variable-wrapper")
              .attr("contenteditable", null)
          })
          editor.on("ExecCommand", function(e) {
            $(editor.getBody())
              .find(".variable-wrapper")
              .attr("contenteditable", false)
          })
          editor.ui.registry.addButton("verifyButton", {
            text: "审批",
            tooltip: "添加审批流",
            onAction: () => {
              editor.insertContent(toDateHtml())
            }
          })
        },
        init_instance_callback: function(editor) {
          editor.formatter.get("forecolor")[0].exact = true
          editor.formatter.get("hilitecolor")[0].exact = true
        }
      }
    }
  },
  watch: {
    value(newValue) {
      this.editorContent = newValue
    },
    editorContent(newValue) {
      this.$emit("update:value", newValue)
    }
  },
  mounted() {
    let vue = this
    tinymce.init({}).then(() => {
      this.editor = tinymce.editors[this.id]
      this.editor.on("dynamicTable", function(e) {
        vue.openDialog(this)
      })
      this.editor.on("watermark", function(e) {
        vue.setWatermark(this)
      })
    })
  },
  methods: {
    // 打印
    print() {
      this.editor.execCommand("mcePrint")
    },
    // 预览
    preview() {
      this.editor.execCommand("mcePreview")
    },
    // 向富文本中添加变量
    addVar(sheetInfo, field, display = null) {
      // 获取插入位置的节点
      let node = this.editor.selection.getNode()
      // 不在表格内
      if (node.localName != "td") {
        if (sheetInfo.refControlCid && !sheetInfo.oneFlag) {
          this.$message({ message: "关联多条数据只能插入动态表格", offset: 100 })
          return
        }
        this.insertContent(sheetInfo, field, display)
        return
      }
      // 获取表格节点
      let temp = node
      while (temp.localName != "table") {
        temp = temp.parentNode
      }
      let table = temp
      // 普通表格内插入
      if (table.dataset.type != "refTable") {
        if (sheetInfo.refControlCid && !sheetInfo.oneFlag) {
          this.$message({ message: "关联多条数据只能插入动态表格", offset: 100 })
          return
        }
        this.insertContent(sheetInfo, field, display)
        return
      }
      // 动态表格内只能插入关联多条数据
      if (!(sheetInfo.refControlCid && !sheetInfo.oneFlag)) {
        this.$message({ message: "动态表格中只能插入关联多条数据", offset: 100 })
        return
      }
      // 动态表格内只能插入已经设置的关联表数据
      if (table.dataset.value != sheetInfo.refControlCid) {
        this.$message({
          message: `该动态表格中只能插入 ${table.dataset.name}`,
          offset: 100
        })
        return
      }
      // 动态表格内插入
      this.dynamicTableInsert(node, sheetInfo, field, display)
    },
    // 向富文本中插入文本节点
    insertContent(sheetInfo, field, display) {
      let { value, label } = this.getInsertValue(sheetInfo, field)
      let type = field.type
      let content = ""
      if (type == "sign") {
        content = `<img data-control-type="${type}" alt="${label}" src="https://new-service.oss-cn-hangzhou.aliyuncs.com/system/sign_placeholder.png" data-type="var" data-value="${value}"/>`
      } else if (display === "BAR" || display === "QR") {
        content = `<img data-control-type="${type}" alt="${label}" src="https://new-service.oss-cn-hangzhou.aliyuncs.com/system/${display}.png" data-type="var" data-value="${value}" data-display="${display}" />`
      } else {
        content = `<a data-control-type="${type}" class="variable-wrapper" contenteditable="false" data-type="var" data-value="${value}" style="display: inline-block;padding: 0 5px;outline-width:1px;color: #3487e2;background-color: #beddff;border: solid 1px #3487e2;border-radius: 3px;">${label}</a>`
      }
      this.editor.insertContent(content)
    },
    // 向富文本中插入文本节点的value；sheetInfo.type == "globalVar" 代表是全局变量
    getInsertValue(sheetInfo, field) {
      let value, label
      if (sheetInfo.refControlCid) {
        value = `${sheetInfo.refControlCid}.${field.value}`
        label = `${sheetInfo.name} - ${field.label}`
      } else {
        value = field.value
        label = field.label
      }
      return { value, label }
    },
    // 向富文本中的动态表格插入
    dynamicTableInsert(node, sheetInfo, field, display) {
      let { cellIndex } = node
      let { rowIndex } = node.parentNode
      let tBody = node.parentNode.parentNode
      if (rowIndex === 0) {
        node.innerHTML = ""
        let row = `${field.label}`
        tBody.childNodes[rowIndex ? 0 : 1].childNodes[cellIndex].innerHTML = this.getVarStr(
          field.value,
          sheetInfo.name + " - " + field.label,
          display,
          "refVar"
        )
        this.editor.insertContent(row)
      } else {
        node.innerHTML = ""
        let row = this.getVarStr(
          field.value,
          sheetInfo.name + " - " + field.label,
          display,
          "refVar"
        )
        tBody.childNodes[rowIndex ? 0 : 1].childNodes[cellIndex].innerHTML = `${field.label}`
        this.editor.insertContent(row)
      }
    },
    getVarStr(value, label, display, type = "var") {
      if (display === "BAR" || display === "QR") {
        return `<img alt="${label}" src="https://new-service.oss-cn-hangzhou.aliyuncs.com/system/${display}.png" data-type="${type}" data-value="${value}" data-display="${display}" />`
      } else {
        return `<a class="variable-wrapper" contenteditable="false" data-type="${type}" data-value="${value}" style="display: inline-block;padding: 0 5px;outline-width:1px;color: #3487e2;background-color: #beddff;border: solid 1px #3487e2;border-radius: 3px;">${label}</a>`
      }
    },
    insertVerifyTable() {
      let tableName = "审批流"
      let cols = 8
      let keyRow = "<tr>"
      let varRow = "<tr data-type='refRow'>"
      this.verifyFields.forEach(field => {
        keyRow =
          keyRow + `<td style="width: ${100 / cols}%;text-align: center;">${field.label}</td>`
        varRow =
          varRow +
          `<td style="width: ${100 /
            cols}%;text-align: center;"><a class="variable-wrapper" contenteditable="false" data-type="refVar" data-value="${
            field.value
          }" style="display: inline-block;padding: 0 5px;outline-width:1px;color: #3487e2;background-color: #beddff;border: solid 1px #3487e2;border-radius: 3px;">${
            field.label
          }</a></td>`
      })
      keyRow = keyRow + "</tr>"
      varRow = varRow + "</tr>"
      let lastRow = `<tfoot><tr><td colspan="${cols}" align="center" contenteditable="false" style="padding: 12px;background-color: #f7f7f7;font-size:12px;color: #999;">表格行数会根据实际数据量自动加载，数据源：${tableName}</td></tr></tfoot>`
      let table =
        `<table style="width: 100%;" border="1" data-type="verifyTable" data-value="verify" data-name="${tableName}"><tbody>` +
        keyRow +
        varRow +
        lastRow +
        "</tbody></table>"
      return table
    },
    // 新建、设置动态表格
    openDialog(editor) {
      let vue = this
      editor.windowManager.open({
        title: "插入动态表格",
        body: {
          type: "panel",
          items: [
            {
              type: "selectbox", // component type
              name: "refcid", // identifier
              label: "数据源",
              size: 1, // number of visible values (optional)
              items: vue.refSheets
            },
            {
              type: "input",
              name: "cols",
              label: "列数",
              value: 5
            }
          ]
        },
        buttons: [
          {
            type: "cancel",
            text: "Close"
          },
          {
            type: "submit",
            text: "Save",
            primary: true
          }
        ],
        onSubmit: function(api) {
          let data = api.getData()
          if (!data.refcid || !data.cols) {
            let msg = ""
            if (!data.refcid) msg = "请选择数据源"
            else if (!data.cols) msg = "请输入列数"
            vue.$message({ message: msg, offset: 100 })
            return
          }
          let tableName = vue.refSheets.find(i => i.value == data.refcid).text
          let keyRow =
            "<tr>" +
            `<td style="width: ${100 / data.cols}%;text-align: center;">序号</td>` +
            `<td style="width: ${100 / data.cols}%;text-align: center;"></td>`.repeat(
              data.cols - 1
            ) +
            "</tr>"
          let varRow =
            "<tr data-type='refRow'>" +
            `<td style="width: ${100 /
              data.cols}%;text-align: center;"><a class="variable-wrapper" contenteditable="false" data-type="refVar" data-value="idx" style="display: inline-block;padding: 0 5px;outline-width:1px;color: #3487e2;background-color: #beddff;border: solid 1px #3487e2;border-radius: 3px;">序号</a></td>` +
            `<td style="width: ${100 / data.cols}%;text-align: center;"></td>`.repeat(
              data.cols - 1
            ) +
            "</tr>"
          let lastRow = `<tfoot><tr><td colspan="${data.cols}" align="center" contenteditable="false" style="padding: 12px;background-color: #f7f7f7;font-size:12px;color: #999;">表格行数会根据实际数据量自动加载，数据源：${tableName}</td></tr></tfoot>`
          let table =
            `<table style="width: 100%;" border="1" data-type="refTable" data-value="${data.refcid}" data-name="${tableName}"><tbody>` +
            keyRow +
            varRow +
            lastRow +
            "</tbody></table>"
          // 插入表格并聚焦
          editor.selection.setCursorLocation(editor.insertContent(table))
          api.close()
        }
      })
    },
    // 设置水印
    setWatermark(editor) {
      let vue = this
      let editId = window.location.search.split("?id=")[1]
      // 获取存储的水印信息
      let img = [localStorage.getItem(`${editId}-WatermarkUrl`)] ?? []
      let size = localStorage.getItem(`${editId}-WatermarkSize`) ?? ""
      let trans = localStorage.getItem(`${editId}-WatermarkTrans`) ?? ""
      editor.windowManager.open({
        title: "设置水印",
        body: {
          type: "panel",
          items: [
            {
              type: "dropzone", // component type
              name: "img", // identifier
              label: "上传图片"
            },
            {
              type: "input",
              name: "size",
              label: "大小"
            },
            {
              type: "input",
              name: "trans",
              label: "透明度"
            }
          ]
        },
        buttons: [
          {
            type: "custom",
            text: "清空"
          },
          {
            type: "cancel",
            text: "Close"
          },
          {
            type: "submit",
            text: "Save",
            primary: true
          }
        ],
        initialData: { size: size, img: img, trans: trans },
        // 清空水印
        onAction: function(api) {
          localStorage.removeItem(`${editId}-WatermarkUrl`)
          localStorage.removeItem(`${editId}-WatermarkSize`)
          localStorage.removeItem(`${editId}-WatermarkTrans`)
          if (editor.dom.doc.querySelector(".watermark") != null) {
            editor.dom.doc.querySelector(".watermark").remove()
          }
          api.close()
        },
        onSubmit: async function(api) {
          let data = api.getData()
          if (data.img.length == 0 || !data.size || !data.trans) {
            let msg = ""
            if (data.img.length == 0) msg = "请选择水印图片"
            else if (!data.size) msg = "请输入大小"
            else if (!data.trans) msg = "请输入透明度"
            vue.$message({ message: msg, offset: 100 })
            api.close()
            return
          }
          // 如果当前未选新图片背景，则从local中取数据，如果已选新文件，则进行相应处理
          if (typeof data.img[0] == "string") {
            data.img = img
          } else {
            // 处理图片，上传到oss中
            let fileName = data.img[0].lastModified + "_" + randomString(8) + "_" + data.img[0].name
            let uploadPath = "print-template/" + fileName
            await uploadToOss(data.img[0], uploadPath)
              .then(ossRes => {
                data.img = [ossRes.url]
              })
              .catch(err => {
                console.log(err)
                return { result: false, msg: data.img[0].name + " 上传失败, 请重新上传！" }
              })
          }
          localStorage.setItem(`${editId}-WatermarkUrl`, data.img[0])
          localStorage.setItem(`${editId}-WatermarkSize`, data.size)
          localStorage.setItem(`${editId}-WatermarkTrans`, data.trans)
          // 插入图片置于底层
          let imgBg = `<div class="watermark">
            <img src="${data.img[0]}" style="width:${data.size}%;opacity:${data.trans / 100}"></div>`
          // 当模版中已有水印的时候，替换水印节点内容；为否则添加节点
          if (editor.dom.doc.querySelector(".watermark") != null) {
            editor.dom.doc.querySelector(".watermark").remove()
            editor.insertContent(imgBg)
          } else editor.insertContent(imgBg)
          api.close()
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.tinymce-editor {
  width: 100%;
  height: calc(100vh - 56px);
  box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.2);
  font-size: 14px;
  ::v-deep {
    .variable-wrapper {
      cursor: default !important;
      display: inline-block;
      margin: 2px;
      border-radius: 3px;
      border: solid 1px transparent;
      user-select: none;
      line-height: 16px;
      box-sizing: border-box;
      color: #3487e2;
      background-color: #beddff;
      border: solid 1px #3487e2;
    }

    .tox-edit-area {
      justify-content: center;
      overflow: auto !important;
      background-color: #f2f2f2 !important;
    }

    .tox-edit-area__iframe {
      height: 100% !important;
      min-height: calc(100vh - 200px) !important;
      width: 790px !important;
      margin: 0 auto !important;
      padding: 0 30px;
      background-color: #f2f2f2 !important;
    }
  }
}
</style>
